import { type FormEvent, useEffect, useState } from 'react'
import { api, type Buchung, type Konto, type KontoArt } from '../api'
import { betrag, datum } from '../format'
import { Dialog, Fehlermeldung, fehlerText } from './gemeinsam'

function Kontotabelle({
  titel,
  konten,
  onBearbeiten,
  onLoeschen,
  onBuchungen,
}: {
  titel: string
  konten: Konto[]
  onBearbeiten: (k: Konto) => void
  onLoeschen: (k: Konto) => void
  onBuchungen: (k: Konto) => void
}) {
  const summe = konten.reduce((s, k) => s + k.saldo, 0)
  return (
    <section className="karte">
      <h2>{titel}</h2>
      <table>
        <thead>
          <tr>
            <th>Bezeichnung</th>
            <th className="zahl">Eingang</th>
            <th className="zahl">Ausgang</th>
            <th className="zahl">Saldo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {konten.map((k) => (
            <tr key={k.id}>
              <td>{k.bezeichnung}</td>
              <td className="zahl">{betrag(k.eingang)}</td>
              <td className="zahl">{betrag(k.ausgang)}</td>
              <td className={`zahl ${k.saldo < 0 ? 'negativ' : ''}`}>{betrag(k.saldo)}</td>
              <td className="aktionen">
                <button className="link" onClick={() => onBuchungen(k)}>
                  Buchungen
                </button>
                <button className="link" onClick={() => onBearbeiten(k)}>
                  Bearbeiten
                </button>
                <button className="link gefahr" onClick={() => onLoeschen(k)}>
                  Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Summe</td>
            <td />
            <td />
            <td className="zahl">{betrag(summe)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </section>
  )
}

function Buchungsliste({ titel, buchungen }: { titel: string; buchungen: Buchung[] }) {
  return (
    <>
      <h3>
        {titel} ({betrag(buchungen.reduce((s, b) => s + b.wert, 0))})
      </h3>
      <table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Name</th>
            <th>Beschreibung</th>
            <th className="zahl">Betrag</th>
          </tr>
        </thead>
        <tbody>
          {buchungen.map((b) => (
            <tr key={b.id}>
              <td>{datum(b.datum)}</td>
              <td>{b.name}</td>
              <td>{b.beschreibung}</td>
              <td className="zahl">{betrag(b.wert)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export function Konten() {
  const [konten, setKonten] = useState<Konto[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [bearbeiten, setBearbeiten] = useState<{ id: number | null; art: KontoArt; bezeichnung: string } | null>(null)
  const [details, setDetails] = useState<{ konto: Konto; ausgaenge: Buchung[]; eingaenge: Buchung[] } | null>(null)

  const laden = () => api.konten().then(setKonten).catch((e) => setFehler(fehlerText(e)))
  useEffect(() => {
    laden()
  }, [])

  async function speichern(e: FormEvent) {
    e.preventDefault()
    if (!bearbeiten) return
    try {
      const daten = { art: bearbeiten.art, bezeichnung: bearbeiten.bezeichnung }
      if (bearbeiten.id) await api.kontoAendern(bearbeiten.id, daten)
      else await api.kontoAnlegen(daten)
      setBearbeiten(null)
      await laden()
    } catch (err) {
      setFehler(fehlerText(err))
    }
  }

  async function loeschen(k: Konto) {
    if (!confirm(`Konto „${k.bezeichnung}“ löschen?`)) return
    try {
      await api.kontoLoeschen(k.id)
      await laden()
    } catch (err) {
      setFehler(fehlerText(err))
    }
  }

  async function buchungen(k: Konto) {
    try {
      setDetails({ konto: k, ...(await api.kontoBuchungen(k.id)) })
    } catch (err) {
      setFehler(fehlerText(err))
    }
  }

  const tabelle = (art: KontoArt, titel: string) => (
    <Kontotabelle
      titel={titel}
      konten={konten.filter((k) => k.art === art)}
      onBearbeiten={(k) => setBearbeiten({ id: k.id, art: k.art, bezeichnung: k.bezeichnung })}
      onLoeschen={loeschen}
      onBuchungen={buchungen}
    />
  )

  return (
    <>
      <div className="werkzeuge">
        <button className="primaer" onClick={() => setBearbeiten({ id: null, art: 'AKTIVA', bezeichnung: '' })}>
          Neues Konto
        </button>
      </div>
      <Fehlermeldung fehler={fehler} />
      <div className="zweispaltig gleich">
        {tabelle('AKTIVA', 'Aktiva')}
        {tabelle('PASSIVA', 'Passiva')}
      </div>

      <Dialog offen={bearbeiten != null} titel={bearbeiten?.id ? 'Konto bearbeiten' : 'Neues Konto'} onSchliessen={() => setBearbeiten(null)}>
        {bearbeiten && (
          <form className="formular" onSubmit={speichern}>
            <fieldset className="radios">
              {(['AKTIVA', 'PASSIVA'] as const).map((a) => (
                <label key={a}>
                  <input type="radio" checked={bearbeiten.art === a} onChange={() => setBearbeiten({ ...bearbeiten, art: a })} />{' '}
                  {a === 'AKTIVA' ? 'Aktiva' : 'Passiva'}
                </label>
              ))}
            </fieldset>
            <label>
              Bezeichnung
              <input required maxLength={200} value={bearbeiten.bezeichnung} onChange={(e) => setBearbeiten({ ...bearbeiten, bezeichnung: e.target.value })} />
            </label>
            <div className="knoepfe">
              <button type="submit" className="primaer">
                Speichern
              </button>
            </div>
          </form>
        )}
      </Dialog>

      <Dialog offen={details != null} titel={`Konto ${details?.konto.bezeichnung ?? ''}`} onSchliessen={() => setDetails(null)}>
        {details && (
          <>
            <Buchungsliste titel="Ausgänge" buchungen={details.ausgaenge} />
            <Buchungsliste titel="Eingänge" buchungen={details.eingaenge} />
          </>
        )}
      </Dialog>
    </>
  )
}
